import { TabPanel } from '@mui/lab';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Box } from '@mui/system';
import { LayoutItemStyle } from 'api/models/FormTemplate';
import { FormikProps } from 'formik';
import { MuiColorInput } from 'mui-color-input';
import { useTranslation } from 'react-i18next';

function getValue(value?: string) {
  if (typeof value === 'string') {
    return value.replaceAll('px', '');
  }

  return value;
}

const exampleColors = [
  '#fff5003d',
  '#dd1e1e3f',
  '#00ff0033',
  '#0000ff33',
  '#00000033',
  '#ff00ff33',
  '#ff000033',
  '#00ffff33',
] as const;

interface LayoutConfigTabProps {
  formik: FormikProps<LayoutItemStyle>;
  isRepeatingFieldsEnabled: boolean;
  disabled?: boolean;
}

export default function LayoutConfigTab({
  formik,
  disabled,
  isRepeatingFieldsEnabled,
}: LayoutConfigTabProps) {
  const { t } = useTranslation();
  return (
    <TabPanel
      value="1"
      sx={{
        pl: 0,
        pr: 0,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MuiColorInput
            disabled={disabled}
            fullWidth
            label={t('dialogs.editLayoutField.backgroundColor')}
            name="backgroundColor"
            value={formik.values.backgroundColor as string}
            onChange={value => formik.setFieldValue('backgroundColor', value)}
            format="hex8"
            sx={{ mt: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          {exampleColors.map(color => (
            <Box
              key={color}
              sx={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
                mr: 1,
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease-in-out',
                ':hover': {
                  boxShadow: '0px 0px 2px 2px rgba(0,0,0,0.1)',
                },
              }}
              onClick={() => {
                formik.setFieldValue('backgroundColor', color);
              }}
            />
          ))}
        </Grid>

        <Divider sx={{ mt: 1, mb: 2, width: '100%' }} />

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.paddingTop')}
            type="number"
            name="paddingTop"
            value={getValue(formik.values.paddingTop)}
            error={
              formik.touched.paddingTop && Boolean(formik.errors.paddingTop)
            }
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={formik.touched.paddingTop && formik.errors.paddingTop}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.paddingBottom')}
            type="number"
            name="paddingBottom"
            value={getValue(formik.values.paddingBottom)}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.paddingLeft')}
            type="number"
            name="paddingLeft"
            value={getValue(formik.values.paddingLeft)}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.paddingRight')}
            type="number"
            name="paddingRight"
            value={getValue(formik.values.paddingRight)}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={
              formik.touched.paddingRight && formik.errors.paddingRight
            }
          />
        </Grid>

        <Divider sx={{ mt: 4, mb: 2, width: '100%' }} />

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.marginTop')}
            type="number"
            name="marginTop"
            value={getValue(formik.values.marginTop)}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={formik.touched.marginTop && formik.errors.marginTop}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.marginBottom')}
            type="number"
            name="marginBottom"
            value={getValue(formik.values.marginBottom)}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={
              formik.touched.marginBottom && formik.errors.marginBottom
            }
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.marginLeft')}
            type="number"
            name="marginLeft"
            value={getValue(formik.values.marginLeft)}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={formik.touched.marginLeft && formik.errors.marginLeft}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('dialogs.editLayoutField.marginRight')}
            type="number"
            name="marginRight"
            value={getValue(formik.values.marginRight)}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={formik.touched.marginRight && formik.errors.marginRight}
          />
        </Grid>
        {isRepeatingFieldsEnabled && (
          <Grid item xs={12}>
            <FormControlLabel
              disabled={disabled}
              control={<Checkbox />}
              label={`${t('forms.fieldAttributes.isRepeatable')}`}
              checked={formik.values.isRepeatable}
              name="isRepeatable"
              onChange={e => {
                //@ts-ignore
                formik.setFieldValue('isRepeatable', e.target.checked);
              }}
            />
          </Grid>
        )}
      </Grid>
    </TabPanel>
  );
}
